import {useState} from 'react'
import cn from 'clsx'
import {Space} from 'antd'

import styles from './CashflowMainStat.module.scss'
import {
  Box,
  Paper,
  Meter,
  IconStat,
  Text,
  Loader,
  CashflowIcon,
  RotateArrowThirdIcon,
} from 'common/components'
import { Button } from 'common/components/V2'
import {
  formatMoney,
  totalNumberFromArray,
  isItemDrawdown,
  isItemRepayment,
  maybePluralize,
  createReport,
  countPercentage,
  formatDate,
} from 'common/helpers'
import {
  CURRENT_WEEK_PERIOD,
  PREV_WEEK_PERIOD,
  PREV_MONTH_PERIOD,
  PREV_TWO_MONTHS_PERIOD,
  NEXT_WEEK_PERIOD,
  NEXT_MONTH_PERIOD,
  NEXT_TWO_MONTHS_PERIOD,
} from 'common/constants'
import {
  CashflowPanel,
  CashflowList,
  PeriodSelect,
  useGetCashflow,
  getCurrentPeriod,
  createCustomPeriod,
} from '..'

interface LineStatProps {
  drawdown?: boolean
  value: string
  label: string
}

interface ContentProps {
  cashflow: any
  period: any
}

export default function CashflowMainStat() {
  const [customPeriod, setCustomPeriod] = useState<any[]>()
  const periods = [
    CURRENT_WEEK_PERIOD,
    PREV_WEEK_PERIOD,
    PREV_MONTH_PERIOD,
    PREV_TWO_MONTHS_PERIOD,
    NEXT_WEEK_PERIOD,
    NEXT_MONTH_PERIOD,
    NEXT_TWO_MONTHS_PERIOD,
    createCustomPeriod(customPeriod),
  ]
  const [periodKey, setPeriodKey] = useState<string>(periods[0].key)
  const currentPeriod = getCurrentPeriod(periods, periodKey)
  const {data: cashflow} = useGetCashflow(currentPeriod.period)

  return (
    <Paper className={styles.root}>
      <CashflowPanel title="Cashflow">
        <PeriodSelect
          periods={periods}
          periodKey={periodKey}
          customPeriod={customPeriod}
          setPeriodKey={setPeriodKey}
          setCustomPeriod={setCustomPeriod}
        />
      </CashflowPanel>
      <Content cashflow={cashflow} period={currentPeriod} />
    </Paper>
  )
}

const Content = ({cashflow, period}: ContentProps) => {
  if (!cashflow) return <Loader className={styles.loader} />

  const cashflowItems = Object.values(cashflow.items)
    .flat()
    .map((item: any) => {
      const project = cashflow.projects.find(
        (project: any) => project.uuid === item.projectUuid
      )
      // TODO:在业务上是否会有正常情况下查找不到的场景。异常projectUuid: "048c0031-1906-471c-ae6e-d077dd6aa968"
      if(!project)  return {}
      return {
        ...item,
        projectTitle: project.title,
        managerName: cashflow.managers.find(
          (manager: any) => manager.uuid === project.manager.uuid
        ).firstName,
        amount: isItemDrawdown(item) ? item.amount : Math.abs(item.amount),
      }
    })

  const handleReport = () =>
    createReport(
      getReportData(cashflowItems),
      'Cashflow',
      `cashflow-report_${period.period.dateFrom}-${period.period.dateTo}`
    )

  const drawdowns = cashflowItems.filter((item: any) => isItemDrawdown(item))
  const repayments = cashflowItems.filter((item: any) => isItemRepayment(item))
  const drawdownsAmount = totalNumberFromArray(drawdowns, 'amount')
  const transactedDrawdownsAmount = totalNumberFromArray(
    drawdowns.filter((item: any) => item.source === 'transaction'),
    'amount'
  )
  const repaymentsAmount = totalNumberFromArray(repayments, 'amount')
  const transactedRepaymentsAmount = totalNumberFromArray(
    repayments.filter((item: any) => item.source === 'transaction'),
    'amount'
  )
  const cashflowAmount = drawdownsAmount - repaymentsAmount
  const sumAmount = drawdownsAmount + repaymentsAmount
  const drawdownsFactor = drawdownsAmount / sumAmount
  const repaymentsFactor = repaymentsAmount / sumAmount

  return (
    <>
      <div className={styles.block}>
        <IconStat
          row
          icon={<CashflowIcon />}
          value={formatMoney(Math.abs(cashflowAmount), {
            parentheses: cashflowAmount < 0,
          })}
          label={period.tip}
        />
        <div className={cn(styles.block, styles.block_meter)}>
          <LineStat
            drawdown
            value={formatMoney(drawdownsAmount)}
            label={maybePluralize(drawdowns.length, 'drawdown')}
          />
          <div
            className={styles.meters}
            style={{
              gridTemplateColumns: `${drawdownsFactor}fr ${repaymentsFactor}fr`,
            }}
          >
            <Meter
              color="blacky"
              value={countPercentage(
                transactedDrawdownsAmount,
                drawdownsAmount
              )}
            />
            <Meter
              color="greeny"
              value={countPercentage(
                transactedRepaymentsAmount,
                repaymentsAmount
              )}
            />
          </div>
          <LineStat
            value={formatMoney(repaymentsAmount, {parentheses: true})}
            label={maybePluralize(repayments.length, 'repayment')}
          />
        </div>
        <Button $color='cyan' $size='small' onClick={handleReport}>Download</Button>
      </div>
      <CashflowList
        cashflowItems={cashflowItems}
        drawdownsCount={drawdowns.length}
        repaymentsCount={repayments.length}
        drawdownsAmount={drawdownsAmount}
        repaymentsAmount={repaymentsAmount}
      />
    </>
  )
}

const LineStat = ({drawdown, value, label}: LineStatProps) => (
  <Box align={drawdown ? 'right' : undefined}>
    <Space size={6}>
      <RotateArrowThirdIcon className={drawdown ? undefined : styles.icon} />
      <Text size="2xl">{value}</Text>
    </Space>
    <Text size="xs" weight="medium" color="grey">
      {label}
    </Text>
  </Box>
)

const getReportData = (cashflowItems: any[]): any[] =>
  cashflowItems.map((item) => ({
    Date: formatDate(item.date),
    Project: item.projectTitle,
    Source: item.source,
    Type: item.type,
    Amount: item.type === 'repayment' ? -item.amount : item.amount,
    LM: item.managerName,
  }))
